import { useState } from 'react';

interface OnboardingBannerProps {
  totalLabels: number;
}

const DISMISSED_KEY = 'edi-label-onboarding-dismissed';

export function OnboardingBanner({ totalLabels }: OnboardingBannerProps) {
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  });

  // Only show for new tenants with 0 usage
  if (dismissed || totalLabels > 0) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  return (
    <div className="relative bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-6 mb-6">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss welcome banner"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      <h2 className="text-lg font-semibold text-gray-900 mb-3">
        Welcome to EDI Label Service!
      </h2>

      <p className="text-gray-600 mb-4">Here's how to get started:</p>

      <ol className="space-y-2 text-sm text-gray-700">
        <li className="flex items-start gap-2">
          <span className="flex-shrink-0 w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
          <span>Browse your templates on the <a href="/templates" className="text-primary-600 hover:underline font-medium">Templates</a> page</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="flex-shrink-0 w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
          <span>Test a label on the <a href="/test-label" className="text-primary-600 hover:underline font-medium">Test Label</a> page</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="flex-shrink-0 w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
          <span>Integrate with our API — see the <a href="/docs" className="text-primary-600 hover:underline font-medium">Docs</a> page for code examples</span>
        </li>
      </ol>

      <p className="mt-4 text-xs text-gray-500">
        Need help? Email <a href="mailto:support@edi-partners.com" className="text-primary-600 hover:underline">support@edi-partners.com</a>
      </p>
    </div>
  );
}
